import { BaseItem } from '@algolia/autocomplete-core';
import { Node as MDASTNode } from 'unist';

export enum IndexTypes {
  HEADING = 'heading',
  TEXT = 'text',
  PAGE = 'page',
  CODE = 'code',
}

export enum NodeTypes {
  HEADING = 'heading',
  TEXT = 'text',
  INLINECODE = 'inlineCode',
  HTML = 'html',
  PARAGRAPH = 'paragraph',
  TABLECELL = 'tableCell',
  IMAGE = 'image',
  Code = 'code',
}

interface IndexBase {
  // unique ID required by Algolia
  objectID: string;
  // page name
  name: string;
  // text content
  content: string;
  query: string[];
  anchor: string;
  // slug of the current page without query params or anchor
  slug: string;
  product: string;
  version: string;
  // multi platform guides don't have a platform
  platform: string | null;
}

export interface PageIndex extends IndexBase {
  type: IndexTypes.PAGE;
}

export interface HeadingIndex extends IndexBase {
  type: IndexTypes.HEADING;
}

export interface TextIndex extends IndexBase {
  type: IndexTypes.TEXT;
}

export interface CodeIndex extends IndexBase {
  type: IndexTypes.CODE;
}

export type SearchIndex = (PageIndex | HeadingIndex | TextIndex | CodeIndex)[];

export interface TextNode extends MDASTNode {
  type: NodeTypes.TEXT;
  value: string;
}

export interface InlineCodeNode extends MDASTNode {
  type: NodeTypes.INLINECODE;
  value: string;
}

export interface ImageNode extends MDASTNode {
  type: NodeTypes.IMAGE;
  url: string;
  alt: string;
  title: string;
  value?: string;
}

export interface HeadingNode extends MDASTNode {
  type: NodeTypes.HEADING;
  depth: number;
  children: (TextNode | InlineCodeNode)[];
}

export interface ParagraphNode extends MDASTNode {
  type: NodeTypes.PARAGRAPH;
  children: (TextNode | InlineCodeNode | ImageNode)[];
}

export interface CodeNode extends MDASTNode {
  type: NodeTypes.Code;
  value: string | string[];
}

export interface TableCellNode extends MDASTNode {
  type: NodeTypes.TABLECELL;
  children: (TextNode | InlineCodeNode | ImageNode)[];
}

export interface HTMLNode extends MDASTNode {
  type: NodeTypes.HTML;
  value: string;
}

export type QueryList = { start: number; end?: number; query: string }[];

export type DisclaimerList = { start: number; end?: number }[];

export type HeadingList = { start: number; anchor: string; index: number }[];

export type CodeBlockList = {
  start: number;
  end?: number;
  languages: string[];
}[];

interface DocSearchHitAttributeHighlightResult {
  value: string;
  matchLevel: 'full' | 'none' | 'partial';
  matchedWords: string[];
  fullyHighlighted?: boolean;
}

interface DocSearchHitHighlightResult {
  content: DocSearchHitAttributeHighlightResult;
}

interface DocSearchHitAttributeSnippetResult {
  value: string;
  matchLevel: 'full' | 'none' | 'partial';
}

interface DocSearchHitSnippetResult {
  content: DocSearchHitAttributeSnippetResult;
}

export interface DocSearchHit extends BaseItem {
  // unique ID required by Algolia
  objectID: string;
  // page name
  name: string;
  // text content
  content: string;
  anchor: string;
  // slug of the current page without query params or anchor
  slug: string;
  type: IndexTypes;
  query: string[];
  _highlightResult: DocSearchHitHighlightResult;
  _snippetResult: DocSearchHitSnippetResult;
  _rankingInfo?: {
    promoted: boolean;
    nbTypos: number;
    firstMatchedWord: number;
    proximityDistance?: number;
    geoDistance: number;
    geoPrecision?: number;
    nbExactWords: number;
    words: number;
    filters: number;
    userScore: number;
    matchedGeoLocation?: {
      lat: number;
      lng: number;
      distance: number;
    };
  };
  _distinctSeqID?: number;
}
